import { useState } from 'react';
import { INVESTMENT_PORTFOLIO_ROLES, INVESTMENT_REVIEW_CADENCES } from 'shared';
import type {
  InvestmentPortfolioRole,
  InvestmentReviewCadence,
  UpdateInvestmentThesisInput,
} from 'shared';

import type { InvestmentThesisDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface InvestmentThesisFormProps {
  security: { displaySymbol: string; description: string };
  thesis: InvestmentThesisDto | null;
  isPending: boolean;
  idPrefix: string;
  onSave: (input: UpdateInvestmentThesisInput) => void;
}

const fieldClass =
  'mt-1 min-h-24 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/40';

function optionalAllocation(value: string): number | null {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) / 100 : null;
}

function lines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

export function InvestmentThesisForm({
  security,
  thesis,
  isPending,
  idPrefix,
  onSave,
}: InvestmentThesisFormProps) {
  const [summary, setSummary] = useState(thesis?.thesisSummary ?? '');
  const [role, setRole] = useState<InvestmentPortfolioRole | ''>(thesis?.portfolioRole ?? '');
  const [horizon, setHorizon] = useState(thesis?.timeHorizon ?? '');
  const [target, setTarget] = useState(
    thesis?.targetAllocation ? String(Number(thesis.targetAllocation) * 100) : '',
  );
  const [maximum, setMaximum] = useState(
    thesis?.maximumAllocation ? String(Number(thesis.maximumAllocation) * 100) : '',
  );
  const [breakers, setBreakers] = useState(thesis?.thesisBreakers.join('\n') ?? '');
  const [kpis, setKpis] = useState(thesis?.keyKpis.join('\n') ?? '');
  const [bullCase, setBullCase] = useState(thesis?.bullCase ?? '');
  const [baseCase, setBaseCase] = useState(thesis?.baseCase ?? '');
  const [bearCase, setBearCase] = useState(thesis?.bearCase ?? '');
  const [valuationNotes, setValuationNotes] = useState(thesis?.valuationNotes ?? '');
  const [reviewCadence, setReviewCadence] = useState<InvestmentReviewCadence | ''>(
    thesis?.reviewCadence ?? '',
  );
  const [nextReviewAt, setNextReviewAt] = useState(thesis?.nextReviewAt ?? '');

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          thesisSummary: summary.trim() || null,
          portfolioRole: role || null,
          timeHorizon: horizon.trim() || null,
          targetAllocation: optionalAllocation(target),
          maximumAllocation: optionalAllocation(maximum),
          thesisBreakers: lines(breakers),
          keyKpis: lines(kpis),
          bullCase: bullCase.trim() || null,
          baseCase: baseCase.trim() || null,
          bearCase: bearCase.trim() || null,
          valuationNotes: valuationNotes.trim() || null,
          reviewCadence: reviewCadence || null,
          nextReviewAt: nextReviewAt || null,
        });
      }}
    >
      <div>
        <div className="text-sm font-semibold">{security.displaySymbol}</div>
        <div className="text-sm text-text-muted">{security.description}</div>
      </div>

      <label className="block text-sm text-text-muted">
        Thesis summary
        <textarea
          className={fieldClass}
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm text-text-muted">
          Portfolio role
          <select
            className="mt-1 h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
            value={role}
            onChange={(event) => setRole(event.target.value as typeof role)}
          >
            <option value="">Not set</option>
            {INVESTMENT_PORTFOLIO_ROLES.map((value) => (
              <option key={value} value={value}>
                {value.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-text-muted" htmlFor={`${idPrefix}-time-horizon`}>
          Time horizon
          <Input
            id={`${idPrefix}-time-horizon`}
            value={horizon}
            onChange={(event) => setHorizon(event.target.value)}
          />
        </label>
        <label className="text-sm text-text-muted" htmlFor={`${idPrefix}-target-allocation`}>
          Target allocation (%)
          <Input
            id={`${idPrefix}-target-allocation`}
            inputMode="decimal"
            min="0"
            max="100"
            step="0.1"
            type="number"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
          />
        </label>
        <label className="text-sm text-text-muted" htmlFor={`${idPrefix}-maximum-allocation`}>
          Maximum allocation (%)
          <Input
            id={`${idPrefix}-maximum-allocation`}
            inputMode="decimal"
            min="0"
            max="100"
            step="0.1"
            type="number"
            value={maximum}
            onChange={(event) => setMaximum(event.target.value)}
          />
        </label>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {[
          ['Bull case', bullCase, setBullCase],
          ['Base case', baseCase, setBaseCase],
          ['Bear case', bearCase, setBearCase],
        ].map(([label, value, setter]) => (
          <label key={String(label)} className="text-sm text-text-muted">
            {String(label)}
            <textarea
              className={fieldClass}
              value={String(value)}
              onChange={(event) => (setter as (next: string) => void)(event.target.value)}
            />
          </label>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm text-text-muted">
          Thesis breakers (one per line)
          <textarea
            className={fieldClass}
            value={breakers}
            onChange={(event) => setBreakers(event.target.value)}
          />
        </label>
        <label className="text-sm text-text-muted">
          Key KPIs (one per line)
          <textarea
            className={fieldClass}
            value={kpis}
            onChange={(event) => setKpis(event.target.value)}
          />
        </label>
      </div>

      <label className="block text-sm text-text-muted">
        Valuation notes
        <textarea
          className={fieldClass}
          value={valuationNotes}
          onChange={(event) => setValuationNotes(event.target.value)}
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm text-text-muted">
          Review cadence
          <select
            className="mt-1 h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
            value={reviewCadence}
            onChange={(event) => setReviewCadence(event.target.value as typeof reviewCadence)}
          >
            <option value="">Not set</option>
            {INVESTMENT_REVIEW_CADENCES.map((value) => (
              <option key={value} value={value}>
                {value.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-text-muted" htmlFor={`${idPrefix}-next-review`}>
          Next review
          <Input
            id={`${idPrefix}-next-review`}
            type="date"
            value={nextReviewAt}
            onChange={(event) => setNextReviewAt(event.target.value)}
          />
        </label>
      </div>

      <Button type="submit" disabled={isPending}>
        {isPending ? 'Saving...' : thesis ? 'Save thesis' : 'Create draft'}
      </Button>
    </form>
  );
}
